import { useState, useEffect, useCallback, useMemo } from "react";
import { Mail, ToggleLeft, ToggleRight, Save, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_MAP = {
  "User Notifications": [
    "user_registration",
    "class_enrollment_user",
    "hour_status_update",
    "career_application_user",
  ],
  "Admin Notifications": [
    "class_enrollment_admin",
    "new_referral",
    "new_contact",
    "hour_submitted",
    "career_application_admin",
  ],
};

export const EmailSettingsPanel = ({ getAuthHeader }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/email-settings`, getAuthHeader());
      setNotifications(res.data.notifications);
      setDirty(false);
    } catch {
      toast.error("Failed to load email settings");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const toggleSetting = (key) => {
    setNotifications((prev) =>
      prev.map((n) => (n.key === key ? { ...n, enabled: !n.enabled } : n))
    );
    setDirty(true);
  };

  const toggleAll = (enabled) => {
    setNotifications((prev) => prev.map((n) => ({ ...n, enabled })));
    setDirty(true);
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const settings = {};
      notifications.forEach((n) => {
        settings[n.key] = n.enabled;
      });
      await axios.put(`${API}/admin/email-settings`, { settings }, getAuthHeader());
      toast.success("Email settings saved");
      setDirty(false);
    } catch {
      toast.error("Failed to save email settings");
    } finally {
      setSaving(false);
    }
  };

  const enabledCount = useMemo(() => notifications.filter((n) => n.enabled).length, [notifications]);
  const totalCount = notifications.length;

  const notificationsByCategory = useMemo(() => {
    const result = {};
    Object.entries(CATEGORY_MAP).forEach(([category, keys]) => {
      result[category] = notifications.filter((n) => keys.includes(n.key));
    });
    return result;
  }, [notifications]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16" data-testid="email-settings-loading">
        <Loader2 className="w-6 h-6 animate-spin text-warm-600" />
        <span className="ml-2 text-gray-500">Loading email settings...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="email-settings-panel">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Mail className="w-5 h-5 text-warm-600" />
            Email Notifications
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            {enabledCount} of {totalCount} notifications active
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => toggleAll(true)}
            data-testid="enable-all-btn"
            className="text-xs"
          >
            Enable All
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => toggleAll(false)}
            data-testid="disable-all-btn"
            className="text-xs"
          >
            Disable All
          </Button>
          <Button
            size="sm"
            onClick={saveSettings}
            disabled={!dirty || saving}
            data-testid="save-email-settings-btn"
            className="bg-warm-700 hover:bg-warm-800 text-white gap-1"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save
          </Button>
        </div>
      </div>

      {/* Category sections */}
      {Object.entries(notificationsByCategory).map(([category, items]) => (
        <div key={category}>
          <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">
            {category}
          </h4>
          <div className="space-y-2">
            {items.map((n) => (
                <div
                  key={n.key}
                  data-testid={`toggle-${n.key}`}
                  className={`flex items-center justify-between px-4 py-3 rounded-lg border transition-colors cursor-pointer ${
                    n.enabled
                      ? "bg-warm-50/50 border-warm-200"
                      : "bg-gray-50 border-gray-200"
                  }`}
                  onClick={() => toggleSetting(n.key)}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <Mail
                      className={`w-4 h-4 flex-shrink-0 ${
                        n.enabled ? "text-warm-600" : "text-gray-400"
                      }`}
                    />
                    <span
                      className={`text-sm font-medium ${
                        n.enabled ? "text-gray-900" : "text-gray-500"
                      }`}
                    >
                      {n.label}
                    </span>
                  </div>
                  {n.enabled ? (
                    <ToggleRight className="w-7 h-7 text-warm-600 flex-shrink-0" />
                  ) : (
                    <ToggleLeft className="w-7 h-7 text-gray-400 flex-shrink-0" />
                  )}
                </div>
              ))}
          </div>
        </div>
      ))}

      {/* Info box */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <p className="text-sm text-amber-800">
          <strong>Note:</strong> Emails are sent via SendGrid from{" "}
          <span className="font-mono text-xs">director@thepillarprojectbaltimore.org</span>.
          Disabled notifications will be silently skipped. Changes take effect immediately after saving.
        </p>
      </div>
    </div>
  );
};
